import { useEffect, useRef, useState, useCallback } from 'react';
import { useUser } from '@clerk/clerk-react';

/**
 * usePresence — Trackea qué agentes están viendo qué recurso (conversación / hilo)
 * en tiempo real vía el WebSocket de workers del chat.
 *
 * Puede recibir un `wsRef` externo (ChatPage ya lo mantiene) para reutilizar la conexión
 * existente, o crear una conexión propia si `wsRef` no se pasa (OrderMessagesPage).
 *
 * @param {object} opts
 * @param {string} opts.orgId               — ID de la organización activa (Clerk)
 * @param {string} opts.resourceKey         — Ej: "chat:<conv_id>" o "order:<thread_id>"
 * @param {object} [opts.externalWsRef]     — Ref a un WebSocket ya conectado (opcional)
 * @param {boolean} [opts.externalWsConnected] — Estado de conexión del WS externo
 *
 * @returns {{
 *   viewersByResource: Record<string, Array<{agent_id, name, email}>>,
 *   viewersOthersOnCurrent: Array<{agent_id, name, email}>,
 *   selfAgentId: string,
 * }}
 */
export function usePresence({ orgId, resourceKey, externalWsRef, externalWsConnected }) {
  const { user: clerkUser } = useUser();
  const selfAgentId = clerkUser?.id || '';
  const selfName = clerkUser?.fullName || clerkUser?.firstName || (clerkUser?.primaryEmailAddress?.emailAddress || 'Agente');
  const selfEmail = clerkUser?.primaryEmailAddress?.emailAddress || '';

  const [viewersByResource, setViewersByResource] = useState({});
  const currentResourceRef = useRef(null);
  const ownWsRef = useRef(null);
  const [ownWsConnected, setOwnWsConnected] = useState(false);

  // ── WebSocket manager: usar external si viene, si no crear uno propio ──
  useEffect(() => {
    if (externalWsRef || !orgId) return; // ChatPage maneja su propia conexión
    const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
    const wsBase = API_BASE.replace(/^https?/, 'wss').replace(/^http/, 'ws');
    let ws, retryTimeout, keepAlive;

    const connect = () => {
      try {
        ws = new WebSocket(`${wsBase}/api/chat/ws/workers/${orgId}`);
      } catch { return; }
      ownWsRef.current = ws;
      ws.onopen = () => setOwnWsConnected(true);
      ws.onerror = () => { try { ws.close(); } catch {} };
      ws.onclose = () => {
        setOwnWsConnected(false);
        clearInterval(keepAlive);
        retryTimeout = setTimeout(connect, 4000);
      };
      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data);
          if (msg.type === 'viewing_update' && msg.resource_key) {
            setViewersByResource(prev => ({ ...prev, [msg.resource_key]: msg.viewers || [] }));
          } else if (msg.type === 'connected' && msg.presence) {
            setViewersByResource(msg.presence || {});
          }
        } catch {}
      };
      keepAlive = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) ws.send('ping');
      }, 25000);
    };
    connect();
    return () => {
      clearTimeout(retryTimeout);
      clearInterval(keepAlive);
      try { ws?.close(); } catch {}
      ownWsRef.current = null;
    };
  }, [orgId, externalWsRef]);

  // ── Escuchar eventos de presencia sobre WS externo (ChatPage) ──
  useEffect(() => {
    if (!externalWsRef) return;
    const ws = externalWsRef.current;
    if (!ws || !externalWsConnected) return;
    const prev = ws.onmessage;
    ws.onmessage = (e) => {
      if (typeof prev === 'function') prev(e);
      try {
        const msg = JSON.parse(e.data);
        if (msg.type === 'viewing_update' && msg.resource_key) {
          setViewersByResource(p => ({ ...p, [msg.resource_key]: msg.viewers || [] }));
        } else if (msg.type === 'connected' && msg.presence) {
          setViewersByResource(msg.presence || {});
        }
      } catch {}
    };
    return () => { if (ws) ws.onmessage = prev; };
  }, [externalWsRef, externalWsConnected]);

  // ── Elegir qué WS usar para enviar mensajes ──
  const getSocket = useCallback(() => {
    if (externalWsRef) return externalWsRef.current;
    return ownWsRef.current;
  }, [externalWsRef]);

  const isConnected = externalWsRef ? !!externalWsConnected : ownWsConnected;

  // ── Enviar viewing_start / viewing_stop cuando cambia el recurso ──
  useEffect(() => {
    if (!orgId || !selfAgentId) return;
    const send = (payload) => {
      const ws = getSocket();
      if (ws && ws.readyState === WebSocket.OPEN) {
        try { ws.send(JSON.stringify(payload)); } catch {}
      }
    };

    // Si estábamos viendo otro recurso, salir de él primero
    const prevKey = currentResourceRef.current;
    if (prevKey && prevKey !== resourceKey) {
      send({ type: 'viewing_stop', resource_key: prevKey, agent_id: selfAgentId });
    }

    if (resourceKey) {
      send({
        type: 'viewing_start',
        resource_key: resourceKey,
        agent_id: selfAgentId,
        name: selfName,
        email: selfEmail,
      });
      currentResourceRef.current = resourceKey;
    } else {
      currentResourceRef.current = null;
    }

    return () => {
      // No enviamos aquí (se maneja en el próximo cambio) para evitar duplicados
    };
  }, [orgId, resourceKey, selfAgentId, selfName, selfEmail, getSocket, isConnected]);

  // ── Al desmontar / cerrar pestaña: enviar stop del recurso actual ──
  useEffect(() => {
    const cleanup = () => {
      const key = currentResourceRef.current;
      const ws = getSocket();
      if (key && ws && ws.readyState === WebSocket.OPEN && selfAgentId) {
        try {
          ws.send(JSON.stringify({ type: 'viewing_stop', resource_key: key, agent_id: selfAgentId }));
        } catch {}
      }
    };
    window.addEventListener('beforeunload', cleanup);
    return () => {
      cleanup();
      window.removeEventListener('beforeunload', cleanup);
    };
  }, [getSocket, selfAgentId]);

  const currentViewers = (resourceKey && viewersByResource[resourceKey]) || [];
  const viewersOthersOnCurrent = currentViewers.filter(v => v.agent_id !== selfAgentId);

  return { viewersByResource, viewersOthersOnCurrent, selfAgentId };
}

/**
 * Devuelve la lista de "otros" agentes (que no soy yo) viendo un recurso concreto.
 * Útil para renderizar indicadores en la lista lateral.
 */
export function getOtherViewers(viewersByResource, resourceKey, selfAgentId) {
  const list = viewersByResource?.[resourceKey] || [];
  return list.filter(v => v.agent_id && v.agent_id !== selfAgentId);
}
